import { AuthSectionType } from './authentication'
import {
  AuthorizationsGroupFinancialExposureFields,
  AuthorizationsGroupFixationsFields,
  AuthorizationsGroupTollingAccountsFields,
} from './authorizationsGroups'
import { FieldsArray } from './types'

export type UserManagementRequest = {
  filter: object
  pagination: {
    start: number
    limit: number
  }
  order: {
    sortField: string
    sortOrder: 'ASC' | 'DESC'
  }
}

export enum USER_STATUS {
  REGISTERED = 'registered', //User signed up by himself, he has already completed the registration form
  CREATED = 'user_created', //User completed
  INVITED = 'invited', //User invited by KME, it has already data inserted
  NEW_USER = 'new_user', //User without data, just signed up. When he logs in, he has to complete the registration form from scratch
}

export enum USER_TYPE {
  CUSTOMER = 'customer',
  SUPPLIER = 'supplier',
  AGENTS = 'agents',
  INTERNAL_USER = 'internal_user',
}

export enum INVITATION_TEMPLATE {
  INVITATION_TEMPLATE_IT = 'invitation_template_it',
  INVITATION_TEMPLATE_DE = 'invitation_template_de',
  INVITATION_TEMPLATE_EN = 'invitation_template_en',
}

export type UserManagementItem = {
  status: USER_STATUS
  registrationDate?: string
  email: string
  firstname: string
  lastname: string
  customerName?: string
}

export type UserManagementFilters = FieldsArray<UserManagementItem>

export type UserManagementData = {
  count: number
  data: UserManagementItem[]
}

export type UserInviteEmailData = {
  to: string
  subject: string
  body: string
  template?: string
}

export type UserInviteUserData = {
  notations: string
  type: USER_TYPE
  association: number[]
}

export type UserPermissions = {
  groupId: number
  resource: Record<AuthSectionType, { type: 'user' | 'user_group' | 'disable'; field: unknown }> & {
    fixations: {
      type: 'user' | 'user_group' | 'disable'
      field: Record<keyof typeof AuthorizationsGroupFixationsFields, number>
    },
    tolling_account: {
      type: 'user' | 'user_group' | 'disable'
      field: Record<keyof typeof AuthorizationsGroupTollingAccountsFields, number>
    },
    financial_exposure: {
      type: 'user' | 'user_group' | 'disable'
      field: Record<keyof typeof AuthorizationsGroupFinancialExposureFields, number>
    }
  }
}

export type UserInviteContactsData = {
  email: string
  description: string
  mail_permission: boolean
  display_permission: boolean
  browse_permission: boolean
}

export type UserInviteRequest = {
  email: UserInviteEmailData
  user: UserInviteUserData
  authorization: UserPermissions
  contacts: UserInviteContactsData[]
}

export type ContactsInfo = {
  label: string
  mail: string
  telephone: string
  mobile: string
}

export type ExtendedContactsInfo = ContactsInfo & {
  mailPermission: boolean
  displayPermission: boolean
  browsePermission: boolean
}

export type UserProfile = {
  businessPartnerId?: number
  businessPartnerName?: string
  businessPartnerVatNumber?: string
  createdAt: string
  email: string
  firstname: string
  lastname: string
  policyAccepted: boolean
}

export type UserDocumentInfo = {
  uploadedAt: string
  fileName: string
  size: number
  author: string
  notes: string
}

export type UploadDocumentRequest = {
  user: string
  notes?: string
  file: File
}

export type UserAccessInfo = {
  user: {
    firstname: string
    lastname: string
  }
  userAccessRequest: {
    businessPartnerName: string
    businessPartnerVatNumber: string
    notations: string
  }
}

export type UserConfirmRequest = {
  user: {
    email: string,
    firstname: string,
    lastname: string,
    notations?: string
    businessPartnerId: number
    type: USER_TYPE
    association: number[]
  }
  authorization: UserPermissions
  contacts: UserInviteContactsData[]
}

export type AssociationsList = {
  email: string
  association: {
    businessPartnerId: number
    businessPartnerName: string
    businessPartnerVatNumber: string
  }[]
}

export type UserAssociationsList = {
  type: USER_TYPE
  associations: number[]
}

export type BusinessPartner = {
  id: number
  name: string
  vatNumber: string
}

export type UserCompanyData = {
  companyShortDescription: string
  companyName: string
  companyId: string
  association: { id: number; name: string, customerId: string }[]
}
